import './styles.scss';

import { QueryInputType, getDiff, handleError, onEnter, queryInput } from '@/utils';
import { SortDirectionEnum, TimeFrame, TimeFrameRange } from '@/models/gen/graphql';
import useConfirmation, { Confirmation } from '@/hooks/useConfirmation';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Datetime } from '@/utils/dates';
import FormField from '@/components/FormField';
import { LoadingBlur } from '@/components/LoadingSpinner';
import Modal from '@/common/Modal';
import TimeFrameDropdown from '@/features/DriveTimeTool/components/TimeFrameDropdown';
import TimeFrameRangeItem from '@/features/DriveTimeTool/components/TimeFrameRangeItem';
import createTimeFrameBulk from '@/features/DriveTimeTool/api/services/createTimeFrameBulk';
import createTimeFrameRangeBulk from '@/features/DriveTimeTool/api/services/createTimeFrameRangeBulk';
import deleteTimeFrameBulk from '@/features/DriveTimeTool/api/services/deleteTimeFrameBulk';
import deleteTimeFrameRangeBulk from '@/features/DriveTimeTool/api/services/deleteTimeFrameRangeBulk';
import { refetchSearchTimeFrameRange } from '@/features/DriveTimeTool/api/services/searchTimeFrameRange';
import updateTimeFrameBulk from '@/features/DriveTimeTool/api/services/updateTimeFrameBulk';
import updateTimeFrameRangeBulk from '@/features/DriveTimeTool/api/services/updateTimeFrameRangeBulk';
import { useDriveTimeTool } from '@/features/DriveTimeTool';

export const DEFAULT_TIME_FRAME_RANGES = [
  {
    id: 'default',
    timeFrameId: null,
    name: 'Late Night',
    startTime: '00:00',
    endTime: '02:00',
  },
  {
    id: 'default',
    timeFrameId: null,
    name: 'Early Morning',
    startTime: '02:00',
    endTime: '04:00',
  },
  {
    id: 'default',
    timeFrameId: null,
    name: 'Dawn Hours',
    startTime: '04:00',
    endTime: '06:00',
  },
  {
    id: 'default',
    timeFrameId: null,
    name: 'Morning',
    startTime: '06:00',
    endTime: '08:00',
  },
  {
    id: 'default',
    timeFrameId: null,
    name: 'Late Morning',
    startTime: '08:00',
    endTime: '10:00',
  },
  {
    id: 'default',
    timeFrameId: null,
    name: 'Mid Morning',
    startTime: '10:00',
    endTime: '12:00',
  },
  {
    id: 'default',
    timeFrameId: null,
    name: 'Midday',
    startTime: '12:00',
    endTime: '14:00',
  },
  {
    id: 'default',
    timeFrameId: null,
    name: 'Afternoon',
    startTime: '14:00',
    endTime: '16:00',
  },
  {
    id: 'default',
    timeFrameId: null,
    name: 'Late Afternoon',
    startTime: '16:00',
    endTime: '18:00',
  },
  {
    id: 'default',
    timeFrameId: null,
    name: 'Evening',
    startTime: '18:00',
    endTime: '20:00',
  },
  {
    id: 'default',
    timeFrameId: null,
    name: 'Late Evening',
    startTime: '20:00',
    endTime: '22:00',
  },
  {
    id: 'default',
    timeFrameId: null,
    name: 'Night',
    startTime: '22:00',
    endTime: '00:00',
  },
];

type TimeFrameModalState = {
  id: string;
  name: string;
  timeFrameRanges: Omit<TimeFrameRange, 'timeFrameId'>[];
};

export const initTimeFrameModalState: TimeFrameModalState = {
  id: null,
  name: '',
  timeFrameRanges: [],
};

const confirmUnsavedChangesOptions: Confirmation.Options = {
  Body: { title: 'Are You Sure?', message: 'You have unsaved changes that will be lost. Are you sure?' },
};

const confirmDeleteOptions: Confirmation.Options = {
  Body: { title: 'Are You Sure?', message: 'You are about to delete this time frame. Are you sure?' },
};

const TimeFrameModal = () => {
  const [show, setShow] = useDriveTimeTool(({ state, setState }) => [state.showTimeFrameModal, setState]);
  const [mode, setMode] = useState<'view' | 'edit'>('view'); // manage edit state // disable inputs on view mode
  const [state, setState] = useState(initTimeFrameModalState); // manage form state
  const [preset, setPreset] = useState('');
  const [loading, setLoading] = useState(false);

  const original = useRef<Omit<TimeFrame, 'userId'> & { timeFrameRanges: Omit<TimeFrameRange, 'timeFrameId'>[] }>(null);

  // if user made unsaved changes we want to display confirmation modal before closing
  const confirmUnsavedChanges = useConfirmation(confirmUnsavedChangesOptions);
  const confirmDelete = useConfirmation(confirmDeleteOptions);

  const changes = () => mode === 'edit';

  const handleSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      if (!original.current?.id) {
        const res = await createTimeFrameBulk([{ name: state.name }]);
        const id = res.output[0]?.node?.id;
        if (!id) throw new Error('Failed to create time frame: id is not defined');
        original.current = { ...state, id };
      }
      // detect changes
      const creates: Array<TimeFrameRange> = [];
      const updates: Array<Partial<TimeFrameRange>> = [];
      const originalMap = {};
      for (const timeFrameRange of original.current.timeFrameRanges) originalMap[timeFrameRange.id] = timeFrameRange;

      const updateMap = {};
      for (const timeFrameRange of state.timeFrameRanges) {
        if (!timeFrameRange.id) {
          creates.push({ ...timeFrameRange, timeFrameId: original.current.id });
          continue;
        }
        updateMap[timeFrameRange.id] = timeFrameRange;
        const [partial] = getDiff(originalMap[timeFrameRange.id], timeFrameRange);
        if (Object.keys(partial).length > 0) {
          updates.push({ ...partial, id: timeFrameRange.id });
          continue;
        }
      }
      const deletes: Array<string> = [];
      for (const timeFrameRange of original.current.timeFrameRanges) {
        if (timeFrameRange.id && !updateMap[timeFrameRange.id]) deletes.push(timeFrameRange.id);
      }

      // promise.all to batch requests
      const requests: Array<Promise<unknown>> = [];
      if (creates.length > 0)
        requests.push(createTimeFrameRangeBulk(creates).catch((err) => console.error('createTimeFrameRangeBulk', err)));
      if (updates.length > 0)
        requests.push(updateTimeFrameRangeBulk(updates).catch((err) => console.error('updateTimeFrameRangeBulk', err)));
      if (deletes.length > 0)
        requests.push(deleteTimeFrameRangeBulk(deletes).catch((err) => console.error('deleteTimeFrameRangeBulk', err)));

      if (state.name !== original.current.name) {
        requests.push(
          updateTimeFrameBulk([{ name: state.name, id: original.current.id }]).catch((err) => console.error('updateTimeFrameBulk', err))
        );
      }

      // we'll for the update incase the time frame name was changed so dropdown will be updated
      await Promise.all(requests);

      setShow((current) => ({
        ...current,
        showTimeFrameModal: !current.showTimeFrameModal,
        timeFrameId:
          current.timeFrameId?.split('||')[0] === original.current.id
            ? `${original.current.id}||${state.name}||${Date.now()}` // append current timestamp to force tables depending on this fields to refresh
            : current.timeFrameId,
      }));
      onResetState();
    } catch (err) {
      console.error('handleSubmit', err);
    } finally {
      setLoading(false);
    }
  };

  const onResetState = () => {
    setPreset('');
    setState(initTimeFrameModalState);
    setMode('view');
    setLoading(false);
    original.current = null;
  };

  const handleHide = async (): Promise<void> => {
    try {
      if (changes()) await confirmUnsavedChanges();
      setShow((current) => ({ ...current, showTimeFrameModal: !current.showTimeFrameModal }));
      onResetState();
    } catch (err) {
      console.error(err);
    }
  };

  const onReset = () => setState({ id: null, name: '', timeFrameRanges: [] });

  // create preset creates from scratch no default
  const onCreatePreset = () => {
    onReset();
    setMode('edit');
    setPreset('');
    original.current = null;
  };

  // focus on name input when in edit mode
  // note: this doesn't work in the onCreatePreset callback since the state is not yet updated to enable the input
  useEffect(() => {
    if (mode === 'edit' && !state.name) (document.querySelector('.TimeFrameModal input[name="timeFrame.name"]') as HTMLElement).focus();
  }, [mode]);

  // onAddTimeFrameRange - add new range that should start with end time of the last range and the end time incremented by last range time diff capped at 24 hours
  const onAddTimeFrameRange = () => {
    setState((current) => {
      const ranges = current.timeFrameRanges;
      const output = {
        id: null,
        name: '',
        startTime: '00:00',
        endTime: '02:00',
      };

      if (ranges.length > 0) {
        const last = ranges[ranges.length - 1];
        const next = getNextRange(last.startTime, last.endTime);
        // no next range if last end time is "00:00" return current
        if (!next) return current;
        output.startTime = next.startTime;
        output.endTime = next.endTime;
      }

      return {
        ...current,
        timeFrameRanges: [...ranges.map((item) => ({ ...item, name: item.name?.trim() })), output],
      };
    });
  };

  const onTimeFrameRangeChange = (index: number) => (name: string, value: string) => {
    switch (name) {
      case 'name':
        setState((current) => {
          const timeFrameRanges = [...current.timeFrameRanges];
          timeFrameRanges[index] = { ...timeFrameRanges[index], name: value };
          return {
            ...current,
            timeFrameRanges,
          };
        });
        break;
      case 'startTime':
        setState((current) => {
          const timeFrameRanges = [...current.timeFrameRanges];
          // if start time is greater than end time, set end time to start time
          timeFrameRanges[index].startTime = value;
          if (isStartTimeGreaterThanEndTime(value, timeFrameRanges[index].endTime)) timeFrameRanges[index].endTime = value;
          return {
            ...current,
            timeFrameRanges,
          };
        });
        break;
      case 'endTime':
        setState((current) => {
          const timeFrameRanges = [...current.timeFrameRanges];
          // if start time is greater than end time, set start time to end time
          timeFrameRanges[index].endTime = value;
          if (isStartTimeGreaterThanEndTime(timeFrameRanges[index].startTime, value)) timeFrameRanges[index].startTime = value;
          return {
            ...current,
            timeFrameRanges,
          };
        });
        break;
    }
  };

  const onRemoveTimeFrameRange = (index: number) => () => {
    setState((current) => {
      return {
        ...current,
        timeFrameRanges: current.timeFrameRanges.filter((_, idx) => idx !== index),
      };
    });
  };

  const onLoadPreset = async (value: string) => {
    try {
      setLoading(true);
      if (!value) value = 'default';
      if (value === preset) return;
      if (value === 'default') {
        setMode('view');
        setState({
          id: null,
          name: `Default ${DEFAULT_TIME_FRAME_RANGES.length} Period(s)`,
          timeFrameRanges: DEFAULT_TIME_FRAME_RANGES,
        });
        setPreset(value);
        original.current = null;
        return;
      }
      const [id, name] = value.split('||');
      setPreset(value);
      // load preset configuration
      const res = await refetchSearchTimeFrameRange(
        [
          {
            timeFrameId: queryInput(id),
            startTime: queryInput(null, QueryInputType.DEFAULT, SortDirectionEnum.Asc, 0),
            endTime: queryInput(null, QueryInputType.DEFAULT, SortDirectionEnum.Asc, 1),
          },
        ],
        { pageSize: null }
      );

      // create new obj different ref
      original.current = {
        id,
        name,
        timeFrameRanges: (res?.rows || []).map((timeFrameRange) => ({
          id: timeFrameRange.id,
          name: timeFrameRange.name,
          startTime: fullTimeToTimeFormat(timeFrameRange.startTime),
          endTime: fullTimeToTimeFormat(timeFrameRange.endTime),
        })),
      };
      // create new obj different ref
      setState({
        id,
        name,
        timeFrameRanges: (res?.rows || []).map((timeFrameRange) => ({
          id: timeFrameRange.id,
          name: timeFrameRange.name,
          startTime: fullTimeToTimeFormat(timeFrameRange.startTime),
          endTime: fullTimeToTimeFormat(timeFrameRange.endTime),
        })),
      });
      setMode('edit');
    } catch (err) {
      console.error('onLoadPreset', err);
    } finally {
      setLoading(false);
    }
  };

  const onDeletePreset = async () => {
    try {
      setLoading(true);
      if (preset === 'default') return console.log('default preset selected');
      const [id] = preset.split('||');
      await confirmDelete();
      // load preset configuration
      await deleteTimeFrameBulk([id]);
      onResetState();
    } catch (err) {
      handleError(err, { notification: { title: 'Delete Time Frames' } });
    } finally {
      setLoading(false);
    }
  };

  const isValid = useMemo(() => {
    if (state.name?.trim() === '') return false;
    if (state.timeFrameRanges.length === 0) return false;
    for (const timeFrameRange of state.timeFrameRanges) {
      if (timeFrameRange.name?.trim() === '') return false;
      if (isStartTimeGreaterThanEndTime(timeFrameRange.startTime, timeFrameRange.endTime)) return false;
    }
    return true;
  }, [state]);

  const Footer = () => (
    <div className="d-flex align-items-center justify-content-between w-100">
      {/* user cannot exceed 24 hours of coverage */}
      <button className="btn btn-success" onClick={onAddTimeFrameRange}>
        <i className="sv sv-plus me-2" />
        Add Time Frame
      </button>
      <button className="btn btn-primary" onClick={handleSubmit} disabled={loading || !isValid}>
        <i className="sv sv-floppy-disk me-2" />
        Apply Changes
      </button>
    </div>
  );

  return (
    <Modal className="TimeFrameModal" show={show} size="lg" footer={mode === 'edit' ? Footer : null} onHide={handleHide}>
      <LoadingBlur loading={loading} size="lg" />
      {/* load the time frame range the and time frames */}
      <h5 className="d-flex align-items-center flex-nowrap gap-2 mb-1">
        <i className="sv sv-clock3" />
        CONFIGURE TIME FRAMES
      </h5>
      <p className="text-muted">
        Customize time periods for drive time analysis. All times use military format (24-Hour) with non-overlapping minutes
      </p>
      <div className="d-flex align-items-center flex-nowrap gap-2 mb-3">
        <TimeFrameDropdown
          className="shadow-box"
          value={preset}
          onChange={onLoadPreset}
          options={{ showClearButton: preset !== 'default' }}
        />
        <button className="btn btn-outline-gray-subtle text-nowrap" onClick={onCreatePreset}>
          <i className="sv sv-plus me-2" />
          Create Preset
        </button>
        {preset !== '' && preset !== 'default' && (
          <button className="btn btn-outline-gray-subtle text-nowrap" onClick={onDeletePreset}>
            <i className="sv sv-trash2 me-2" />
            Delete Preset
          </button>
        )}
        {mode === 'edit' && (
          <button className="btn btn-outline-gray-subtle text-nowrap" onClick={onReset}>
            <i className="sv sv-undo2 me-2" />
            Reset
          </button>
        )}
      </div>
      <div className="d-flex justify-content-between flex-nowrap">
        <div className="d-flex align-items-center flex-nowrap text-nowrap gap-2">
          <div>TIME FRAMES ({state.timeFrameRanges.length}):</div>
          <FormField
            placeholder="Preset Name"
            name="timeFrame.name"
            append={<i className="sv sv-pencil3" />}
            value={state.name}
            condensed
            onChange={(event) => {
              setState((current) => {
                const name = event.target.value;
                return { ...current, name };
              });
            }}
            onKeyDown={onEnter(onAddTimeFrameRange)}
            disabled={mode === 'view' || loading}
          />
        </div>
      </div>
      {/* edit the time frames */}
      <div className="mt-2 d-flex flex-column gap-3">
        {/* each time frame */}
        {state.timeFrameRanges.map((timeFrameRange, index) => (
          <TimeFrameRangeItem
            label={`${index + 1}`}
            startTime={timeFrameRange.startTime}
            endTime={timeFrameRange.endTime}
            name={timeFrameRange.name}
            onChange={onTimeFrameRangeChange(index)}
            onDelete={onRemoveTimeFrameRange(index)}
            disabled={mode === 'view'}
            key={index}
          />
        ))}
      </div>
    </Modal>
  );
};

const getHoursBetween = (startTime: string, endTime: string): number => {
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);

  // Convert times to minutes since start of day
  const startMinutes = startH * 60 + startM;
  let endMinutes = endH * 60 + endM;

  // If end is less than start, assume overnight
  if (endMinutes < startMinutes) {
    endMinutes += 24 * 60;
  }

  const diffMinutes = endMinutes - startMinutes;
  return diffMinutes / 60;
};
// this helper func should return true if the start time is greater than the end time unless end time is "00:00"
const isStartTimeGreaterThanEndTime = (startTime: string, endTime: string): boolean => {
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;
  return startMinutes > endMinutes && endTime !== '00:00';
};

const END_OF_RANGE = new Datetime('2000-01-02T00:00:00');
const getNextRange = (lastStartTime: string, lastEndTime: string): { startTime: string; endTime: string } => {
  // If last end time is "00:00", user already covered 24h → stop
  if (lastEndTime === '00:00') return { startTime: '00:00', endTime: '00:00' };

  // Compute the diff between previous start → end
  const diffHours = getHoursBetween(lastStartTime, lastEndTime);

  // New start = last end
  const startTime = lastEndTime;

  const start = new Datetime(`2000-01-01T${startTime}`);

  // New end = start + diff
  let nextEnd = start.add(diffHours, 'hours');

  // cap end time to end of day -> 24 hours
  if (nextEnd.isAfter(END_OF_RANGE.toString())) nextEnd = END_OF_RANGE;

  const endTime = nextEnd.time;

  return { startTime, endTime };
};

// full time to time
const fullTimeToTimeFormat = (input: string): string => {
  if (!input) return '';
  const [hours, minutes] = input.split(':');
  return `${hours}:${minutes}`;
};

export default TimeFrameModal;
